"use client";

import { Eraser } from "lucide-react";
import type { FileSummary, StripFields } from "@/lib/client-convert";
import type { ConversionTarget } from "@/lib/formats";
import { EditPanel } from "@/components/ui/edit-panel";
import { ToggleChip } from "@/components/ui/toggle-chip";

const FIELD_DEFS: {
  key: keyof StripFields;
  label: string;
  has: (s: FileSummary) => boolean;
}[] = [
  { key: "gps", label: "GPS coordinates", has: (s) => s.hasGps },
  { key: "hr", label: "Heart rate", has: (s) => s.hasHr },
  { key: "power", label: "Power", has: (s) => s.hasPower },
  { key: "cadence", label: "Cadence", has: (s) => s.hasCadence },
  { key: "speed", label: "Speed", has: (s) => s.hasSpeed },
  { key: "altitude", label: "Elevation", has: (s) => s.hasElevation },
  { key: "calories", label: "Calories", has: (s) => s.calories !== undefined },
];

export function FieldStripper({
  summary,
  value,
  onChange,
  target,
  onSwitchTarget,
}: {
  summary: FileSummary;
  value: StripFields;
  onChange: (next: StripFields) => void;
  // The currently selected output format. GPX is a route-only format, so
  // stripping GPS from it yields an empty file; the other targets keep every
  // remaining field. The warning below only applies when GPX is selected.
  target: ConversionTarget;
  onSwitchTarget?: (next: ConversionTarget) => void;
}) {
  const available = FIELD_DEFS.filter((f) => f.has(summary));
  if (!available.length) return null;

  const anyStripped = available.some((f) => value[f.key]);
  const toggle = (key: keyof StripFields) =>
    onChange({ ...value, [key]: !value[key] });

  return (
    <EditPanel
      icon={Eraser}
      title="Remove fields"
      onReset={anyStripped ? () => onChange({}) : undefined}
    >
      <p className="text-sm text-muted-foreground mb-4">
        Strip sensitive metrics before sharing. Unchecked fields are kept.
      </p>

      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
        {available.map((f) => (
          <ToggleChip key={f.key} checked={!!value[f.key]} onClick={() => toggle(f.key)}>
            {f.label}
          </ToggleChip>
        ))}
      </div>

      {value.gps && target === "gpx" && (
        <div className="mt-3 text-xs text-amber-500/90">
          <p>
            You&apos;re exporting as GPX, which only stores the route, so removing GPS would
            leave an empty file. Export as FIT, TCX, or CSV to keep the rest of your data.
          </p>
          {onSwitchTarget && (
            <button
              type="button"
              onClick={() => onSwitchTarget("fit")}
              className="mt-2 rounded-md border border-amber-500/40 px-2 py-1 font-medium text-amber-400 transition-colors hover:bg-amber-500/10"
            >
              Export as FIT instead
            </button>
          )}
        </div>
      )}
    </EditPanel>
  );
}
